import PendingCatalogRequests, { CatalogPublicationFilter, usePendingCatalogRequests } from "../catalogApprovals/PendingCatalogRequests";
import useCatalogAccess from "../useCatalogAccess";
import CatalogCreationPendingNotice, { isPendingCatalogCreation } from "../CatalogCreationPendingNotice";
import { useCatalogTaxonomyText } from "../catalogTaxonomyMessages";
import CorporateRecordBadge from "../CorporateRecordBadge";
import InventoryPolicyChip from "../InventoryPolicyChip";
import { managedByAdmin } from "../../utils/tenderManagement";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import axios from "../../../api/axiosInstance";
import useIsMobile from "../../../hooks/useIsMobile";
import AddFamilyPopup from "../popups/AddFamilyPopup";
import EditTaxonomyPopup from "../popups/EditTaxonomyPopup";
import { TaxonomyMergeActions } from "../popups/TaxonomyMergePopup";
import TaxonomyItemsPopup, {
  makeTaxonomyItemsScope,
  TaxonomyItemsLink,
} from "../popups/TaxonomyItemsPopup";
import {

  buildSearchBlob,
  formatItemsFamiliesDate,
  inventoryPolicyHint,
  inventoryPolicyLabel,
  readItemsFamiliesUser,
  resolveTaxonomyInventoryPolicy,
  truncateItemsFamiliesText,
} from "../itemsFamiliesUi";

function buildFamilySearchBlob(family) {
  return buildSearchBlob([family?.code, family?.name, family?.description]);
}

export default function FamiliesTab({ businessUnitId }) {
  const t = useCatalogTaxonomyText();
  const currentUser = readItemsFamiliesUser();
  const activeBusinessUnitId = businessUnitId || currentUser?.active_business_unit_id;
  const access = useCatalogAccess(activeBusinessUnitId);
  const viewPermission = "purchasing.items.view";
  const editPermissions = ["purchasing.items.create", "purchasing.items.update"];
  const exportPermissions = [viewPermission, "purchasing.items.export"];
  const canViewFamilies = access.can(viewPermission);
  const canCreateFamily = access.can("purchasing.items.create");
  const canEditFamily = access.can(editPermissions);
  const canExport = access.can(exportPermissions);
  const isMobile = useIsMobile();

  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [publicationFilter, setPublicationFilter] = useState("published");
  const pendingCatalog = usePendingCatalogRequests({ entityType: "family", businessUnitId: activeBusinessUnitId,
    enabled: canViewFamilies && publicationFilter === "pending", refreshKey: isAddPopupOpen });
  const [editing, setEditing] = useState(null);
  const [itemsScope, setItemsScope] = useState(null);

  const fetchFamilies = useCallback(
    async ({ quiet = false } = {}) => {
      if (!canViewFamilies) {
        setFamilies([]);
        setLoading(false);
        return;
      }

      if (!quiet) {
        setLoading(true);
      }

      const stamp = access.capture();
      try {
        const options = access.requestConfig(viewPermission);
        const response = await axios.get("/api/purchasing/families", { ...options, params: { ...options.params, catalog: 1 } });
        if (!access.isCurrent(stamp, viewPermission)) return;
        const sorted = [...(response.data || [])].sort((left, right) =>
          String(left?.name || "").localeCompare(String(right?.name || ""), undefined, {
            sensitivity: "base",
          })
        );
        setFamilies(sorted);
      } catch (requestError) {
        if (!access.isCurrent(stamp, viewPermission)) return;
        console.error("[FAMILIES][LOAD][ERROR]", requestError);
        setNotice({
          tone: "error",
          message: "We could not load the family list for this business unit.",
        });
      } finally {
        if (!quiet && access.isCurrent(stamp, viewPermission)) {
          setLoading(false);
        }
      }
    },
    [canViewFamilies, access, viewPermission]
  );

  useEffect(() => {
    fetchFamilies();
  }, [fetchFamilies]);

  const filteredFamilies = useMemo(() => {
    const query = search.trim().toLowerCase();
    return families.filter((family) => !query || buildFamilySearchBlob(family).includes(query));
  }, [families, search]);

  const stats = useMemo(() => {
    const withDescription = filteredFamilies.filter((family) => String(family?.description || "").trim()).length;
    const withCode = filteredFamilies.filter((family) => String(family?.code || "").trim()).length;
    return {
      visible: filteredFamilies.length,
      described: withDescription,
      coded: withCode,
    };
  }, [filteredFamilies]);

  const exportToExcel = async () => {
    if (!access.can(exportPermissions) || publicationFilter === "pending") return;
    const stamp = access.capture();
    try {
      const options = access.requestConfig(exportPermissions);
      const response = await axios.get("/api/purchasing/families", { ...options, params: { ...options.params, catalog: 1, export: 1 } });
      if (!access.isCurrent(stamp, exportPermissions)) return;
      const query = search.trim().toLowerCase();
      const exportRecords = (response.data || []).filter((row) => !query || buildFamilySearchBlob(row).includes(query));
    if (!exportRecords.length) {
      setNotice({
        tone: "info",
        message: "There are no family rows in the current slice to export.",
      });
      return;
    }

    const exportRows = exportRecords.map((family) => ({
      Code: family?.code || "",
      Name: family?.name || "",
      InventoryPolicy: inventoryPolicyLabel(resolveTaxonomyInventoryPolicy(family, "family").policy),
      InventoryPolicySource: inventoryPolicyHint(resolveTaxonomyInventoryPolicy(family, "family"), "family"),
      Description: family?.description || "",
      CreatedAt: family?.created_at || "",
      UpdatedAt: family?.updated_at || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Families");
    XLSX.writeFile(workbook, "families.xlsx");

    setNotice({
      tone: "success",
      message: `${exportRecords.length} family rows exported to Excel.`,
    });
    } catch (_error) {
      if (access.isCurrent(stamp, exportPermissions)) setNotice({ tone: "error", message: "Could not export this catalog. Please try again." });
    }
  };

  const updateInventoryPolicy = async (family, inventoryPolicy) => {
    if (!access.can(editPermissions) || managedByAdmin(family)) return;
    const stamp = access.capture();
    try {
      const response = await axios.put(`/api/purchasing/families/${family.id}`, {
        inventory_policy: inventoryPolicy || null,
      }, access.requestConfig(editPermissions));
      if (!access.isCurrent(stamp, editPermissions)) return;
      if (isPendingCatalogCreation(response.data)) {
        setPendingRequest(response.data.request);
        return;
      }
      setFamilies((current) =>
        current.map((entry) => (Number(entry.id) === Number(family.id) ? { ...entry, ...response.data } : entry))
      );
      setNotice({
        tone: "success",
        message: `${family?.name || "Family"} inventory policy updated.`,
      });
    } catch (requestError) {
      if (!access.isCurrent(stamp, editPermissions)) return;
      console.error("[FAMILIES][POLICY][ERROR]", requestError);
      setNotice({ tone: "error", message: "Could not update this inventory policy." });
      return false;
    }
  };

  const handleAdded = async (newFamily) => {
    await fetchFamilies({ quiet: true });
    setNotice({
      tone: "success",
      message: `${newFamily?.name || "Family"} created successfully.`,
    });
  };

  const handleEdited = (updated) => {
    setFamilies((current) => current.map((entry) => Number(entry.id) === Number(updated.id) ? { ...entry, ...updated } : entry));
    setNotice({ tone: "success", message: `${updated.name} updated successfully.` });
  };

  const renderEditButton = (family) => canEditFamily && !managedByAdmin(family) ? (
    <button type="button" className="spx-btn spx-btn--ghost" aria-label={t(`Edit Family: ${family.name}`)} onClick={() => setEditing(family)}>{t("Edit")}</button>
  ) : null;

  if (!canViewFamilies) {
    return (
      <section className="spx-card pu-taxonomy">

        <div className="spx-empty">{t("You do not have permission to access families.")}</div>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="spx-card pu-taxonomy">

        <div className="spx-empty">{t("Loading families...")}</div>
      </section>
    );
  }

  return (
    <>
      <section className="spx-card pu-taxonomy">


        <div className="spx-head">
          <div className="spx-headMain">
            <div className="spx-kicker">{t("Family → Subfamily → Category → Subcategory → Item")}</div>
            <h2 className="spx-title">{t("Families")}</h2>
            <p className="spx-subtitle">{t("Families contain subfamilies, which organise categories, subcategories and items.")}</p>
          </div>

          <div className="spx-actions">
            <TaxonomyMergeActions kind="family" records={families} businessUnitId={activeBusinessUnitId} onChanged={() => fetchFamilies({ quiet: true })} />
            {canCreateFamily ? (
              <button type="button" className="spx-btn spx-btn--primary" onClick={() => setIsAddPopupOpen(true)}>{t("+ Add Family")} </button>
            ) : null}
            {canExport && <button type="button" className="spx-btn spx-btn--warning" disabled={publicationFilter === "pending"} onClick={exportToExcel}>{t("Export Excel")} </button>}
          </div>
        </div>


        <div className="spx-toolbar">
          <div className="ifm-toolbarGrid ifm-toolbarGrid--two">
            <input
              type="search"
              className="spx-search"
              aria-label={t("Search")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("Search by code, family name or description...")}
            />
            {publicationFilter !== "pending" && <div className="spx-note">{t("Showing")} <strong>{filteredFamilies.length}</strong> {t("of")} <strong>{families.length}</strong> {t("families.")} </div>}
          </div>
        </div>

        <CatalogPublicationFilter value={publicationFilter} onChange={setPublicationFilter} />

        {notice ? <div className={`spx-banner spx-banner--${notice.tone}`}>{t(notice.message)}</div> : null}

        {publicationFilter === "pending" ? <PendingCatalogRequests entityType="family" businessUnitId={activeBusinessUnitId} state={pendingCatalog} search={search} /> : <>
        <div className="spx-stats pu-taxonomy-stats">
          <div className="spx-stat">
            <div className="spx-statLabel">{t("Visible Families")}</div>
            <div className="spx-statValue">{stats.visible}</div>
          </div>
          <div className="spx-stat">
            <div className="spx-statLabel">{t("With Description")}</div>
            <div className="spx-statValue">{stats.described}</div>
          </div>
          <div className="spx-stat">
            <div className="spx-statLabel">{t("With Code")}</div>
            <div className="spx-statValue">{stats.coded}</div>
          </div>
        </div>

        {!filteredFamilies.length ? (
          <div className="spx-empty">{t("No families match the current search.")}</div>
        ) : isMobile ? (
          <div className="spx-mobileList">
            {filteredFamilies.map((family) => (
              <article key={family.id} className="spx-mobileCard">
                {(() => {
                  const policy = resolveTaxonomyInventoryPolicy(family, "family");
                  return (
                    <>
                <div className="spx-mobileTop">
                  <div>
                    <div className="spx-code">{family?.code || `FAMILY-${family.id}`}</div>
                    <TaxonomyItemsLink
                      scope={makeTaxonomyItemsScope("family", family?.id, family?.name)}
                      onOpen={setItemsScope}
                      strong
                      className="spx-strong"
                    >
                      {truncateItemsFamiliesText(family?.name || "-", 48)}<CorporateRecordBadge record={family} />
                    </TaxonomyItemsLink>
                  </div>
                  <InventoryPolicyChip policy={policy.policy} name={family.name} editable={canEditFamily && !managedByAdmin(family)} onChange={(value) => updateInventoryPolicy(family, value)} />
                </div>
                    </>
                  );
                })()}

                <div className="spx-mobileRows">
                  <div>
                    <div className="spx-mobileLabel">{t("Created")}</div>
                    <div className="spx-mobileValue">{formatItemsFamiliesDate(family?.created_at)}</div>
                  </div>
                  <div>
                    <div className="spx-mobileLabel">{t("Updated")}</div>
                    <div className="spx-mobileValue">{formatItemsFamiliesDate(family?.updated_at)}</div>
                  </div>
                </div>

                <div className="spx-mobileValue" style={{ marginTop: 10 }}>
                  {family?.description || "-"}
                </div>
                {canEditFamily && !managedByAdmin(family) && <div className="spx-actions" style={{ marginTop: 10 }}>{renderEditButton(family)}</div>}
              </article>
            ))}
          </div>
        ) : (
          <div className="spx-tableWrap">
            <table className="spx-table ifm-table--compact">
              <thead>
                <tr>
                  <th>{t("Code")}</th>
                  <th>{t("Family")}</th>
                  <th>{t("Inventory Policy")}</th>
                  <th>{t("Description")}</th>
                  <th>{t("Created")}</th>
                  <th>{t("Updated")}</th>
                  {canEditFamily && <th>{t("Actions")}</th>}
                </tr>
              </thead>
              <tbody>
                {filteredFamilies.map((family) => (
                  (() => {
                    const policy = resolveTaxonomyInventoryPolicy(family, "family");
                    return (
                      <tr key={family.id}>
                        <td title={family?.code || `FAMILY-${family.id}`}>
                          <span className="spx-code">{family?.code || `FAMILY-${family.id}`}</span>
                        </td>
                        <td title={family?.name || "-"}>
                          <TaxonomyItemsLink
                            scope={makeTaxonomyItemsScope("family", family?.id, family?.name)}
                            onOpen={setItemsScope}
                            strong
                            className="spx-strong"
                          >
                            {truncateItemsFamiliesText(family?.name || "-", 48)}<CorporateRecordBadge record={family} />
                          </TaxonomyItemsLink>
                        </td>
                        <td>
                          <InventoryPolicyChip policy={policy.policy} name={family.name} editable={canEditFamily && !managedByAdmin(family)} onChange={(value) => updateInventoryPolicy(family, value)} />
                        </td>
                        <td className="spx-muted" title={family?.description || "-"}>
                          {truncateItemsFamiliesText(family?.description || "-", 76)}
                        </td>
                        <td className="spx-muted">{formatItemsFamiliesDate(family?.created_at)}</td>
                        <td className="spx-muted">{formatItemsFamiliesDate(family?.updated_at)}</td>
                        {canEditFamily && <td>{renderEditButton(family)}</td>}
                      </tr>
                    );
                  })()
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>}
      </section>

      {pendingRequest && <CatalogCreationPendingNotice request={pendingRequest} onClose={() => setPendingRequest(null)} />}

      <AddFamilyPopup
        existingRecords={families}
        isOpen={isAddPopupOpen}
        onClose={() => setIsAddPopupOpen(false)}
        onAdded={handleAdded}
      />

      {editing && <EditTaxonomyPopup key={editing.id} kind="family" record={editing} existingRecords={families} onClose={() => setEditing(null)} onSaved={handleEdited} />}

      <TaxonomyItemsPopup scope={itemsScope} onClose={() => setItemsScope(null)} />
    </>
  );
}
